import angular, { IHttpService } from 'angular';
import { Services } from '@Shared.Angular/@types/services';
import { Guid } from '@Shared.Angular/@types/guid';
import ITagGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagGroupDetail';
import { ITagGroupDeleteWarning } from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagGroupDeleteWarning';
import { PubSubEvent } from '@Shared.Angular/@types/pubSub';

export default class TagGroupApiService {
  private tagGroupsApiBaseUri: string;
  private cachedTagGroups: ITagGroupDetail[] = null;
  private subscriberId = 'tagGroupApiService';

  constructor(
    private $http: angular.IHttpService,
    private APP_CONFIG: Services.APP_CONFIG,
    private pubsubService: Services.PubSubService
  ) {
    this.tagGroupsApiBaseUri = this.APP_CONFIG.apiBaseUrl + 'taggroups';

    this.pubsubService.subscribe(
      'TAG_GROUP_CREATED',
      (event: PubSubEvent, tagGroup: ITagGroupDetail) => {
        if (
          !this.cachedTagGroups ||
          this.cachedTagGroups.find((tg) => tg.id === tagGroup.id)
        ) {
          return;
        }
        this.cachedTagGroups.push(tagGroup);
      },
      this.subscriberId
    );

    this.pubsubService.subscribe(
      'TAG_GROUP_UPDATED',
      (event: PubSubEvent, tagGroup: ITagGroupDetail) => {
        if (!this.cachedTagGroups) {
          return;
        }
        const targetGroupIndex = this.cachedTagGroups.findIndex(
          (tg) => tg.id === tagGroup.id
        );
        if (targetGroupIndex > -1) {
          this.cachedTagGroups[targetGroupIndex] = tagGroup;
        } else {
          this.cachedTagGroups.push(tagGroup);
        }
      },
      this.subscriberId
    );

    this.pubsubService.subscribe(
      'TAG_GROUP_DELETED',
      (event: PubSubEvent, tagGroupId: Guid) => {
        if (!this.cachedTagGroups) {
          return;
        }
        const targetGroupIndex = this.cachedTagGroups.findIndex(
          (tg) => tg.id === tagGroupId
        );
        if (targetGroupIndex > -1) {
          this.cachedTagGroups.splice(targetGroupIndex, 1);
        }
      },
      this.subscriberId
    );
  }

  async getAllTagGroups(ignoreCache = false) {
    if (this.cachedTagGroups && !ignoreCache) {
      return structuredClone(this.cachedTagGroups);
    }

    const response = await this.$http.get<ITagGroupDetail[]>(
      this.tagGroupsApiBaseUri
    );
    const tagGroups = (response.data || []).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    tagGroups.forEach((group) => {
      group.tags.sort((a, b) => a.name.localeCompare(b.name));
    });
    this.cachedTagGroups = tagGroups;
    return tagGroups;
  }

  async getById(id: Guid) {
    const response = await this.$http.get<ITagGroupDetail>(
      `${this.tagGroupsApiBaseUri}/${id}`
    );
    response.data.tags.sort((a, b) => a.name.localeCompare(b.name));
    return response.data;
  }

  async getDeleteWarnings(id: Guid) {
    const response = await this.$http.get<ITagGroupDeleteWarning>(
      `${this.tagGroupsApiBaseUri}/${id}/deletewarnings`
    );
    return response.data;
  }

  async delete(id: Guid) {
    await this.$http.delete(`${this.tagGroupsApiBaseUri}/${id}`);
  }

  save(tagGroup: ITagGroupDetail) {
    return this.$http
      .put<Guid>(this.tagGroupsApiBaseUri, tagGroup)
      .then((response) => {
        return response.data;
      });
  }
}

angular
  .module('flowingly.services')
  .factory('tagGroupApiService', [
    '$http',
    'APP_CONFIG',
    'pubsubService',
    (
      $http: IHttpService,
      APP_CONFIG: Services.APP_CONFIG,
      pubsubService: Services.PubSubService
    ) => new TagGroupApiService($http, APP_CONFIG, pubsubService)
  ]);

export type TagGroupApiServiceType = InstanceType<typeof TagGroupApiService>;
